 
/* eslint-disable @typescript-eslint/no-unused-vars */
import { buildingService } from './buildingService';
import { inventoryManagementService } from './inventoryManagementService';
import { manufacturingService } from './manufacturingService';
import { GAME_CONSTANTS, BUILDINGS_CONSTANTS } from '@/config/gameConstants';
import { GAME_FORMULAS } from '@/config/gameFormulas';
import { useProductionStore } from '@/stores/productionStore';
import type { 
  ProductionLine, 
  FactoryProduction, 
  ProductionMetrics,
  ProductionConstraint,
  ProductionSchedule,
  ManufacturingConstraints
} from '@/types/production';
import type { OwnedBuilding } from '@/types/buildings';
import type { Product, Employee } from '@/types/tycoon';
import type { ComponentInventory, DeviceInventory } from '@smartphone-tycoon/shared-core';

export class FactoryProductionService {
  private static instance: FactoryProductionService;
  private productionMetrics: Map<string, ProductionMetrics> = new Map();
  private buildingsCache: Map<string, OwnedBuilding> = new Map();
  
  private constructor() {
    // No need to restore state - Zustand store handles persistence
  }
  
  static getInstance(): FactoryProductionService {
    if (!FactoryProductionService.instance) {
      FactoryProductionService.instance = new FactoryProductionService();
    }
    return FactoryProductionService.instance;
  }
  
  // Get the production store state
  private getStore() {
    return useProductionStore.getState();
  }


  // Initialize production lines for all factory buildings
  initializeProductionLines(buildings: OwnedBuilding[], products?: Product[]): void {
    // console.log('[FactoryProduction] 🏭 Initializing production lines:', {
    //   buildingCount: buildings.length,
    //   productCount: products?.length || 0,
    //   timestamp: new Date().toISOString()
    // });
    
    // Update buildings cache
    this.buildingsCache.clear();
    buildings.forEach(b => this.buildingsCache.set(b.id, b));
    
    const store = this.getStore();
    const productionLines = store.productionLines;
    
    let linesCreated = 0;
    let factoriesFound = 0;
    
    buildings.forEach(building => {
      if (building.isUnderConstruction || building.isUpgrading) {
        // console.log('[FactoryProduction] ⏭️ Skipping building (under construction/upgrading):', building.id);
        return;
      }
      
      const buildingType = buildingService.getBuildingType(building.buildingTypeId);
      if (!buildingType || !buildingType.productionLines) return;
      
      // Include both factory buildings and headquarters with production capabilities
      if (buildingType.category !== 'factory' && buildingType.category !== 'headquarters') return;
      
      factoriesFound++;
      // console.log('[FactoryProduction] 🏭 Found factory building:', {
      //   buildingId: building.id,
      //   buildingType: buildingType.name,
      //   productionLines: buildingType.productionLines,
      //   category: buildingType.category
      // });
      
      // Create production lines for this factory
      for (let i = 1; i <= buildingType.productionLines; i++) {
        const lineId = `${building.id}_line_${i}`;
        
        if (!productionLines.has(lineId)) {
          const newLine: ProductionLine = {
            id: lineId,
            factoryBuildingId: building.id,
            lineNumber: i,
            productId: null,
            allocatedEmployees: 0,
            dailyOutput: 0,
            efficiency: 0,
            qualityScore: 0,
            status: 'idle',
            pauseReason: 'no_product'
          };
          store.updateProductionLine(lineId, newLine);
          linesCreated++;
          // console.log('[FactoryProduction] ➕ Created production line:', lineId);
        } else {
          // Production line already exists, preserve its state
          const existingLine = productionLines.get(lineId)!;
          // Just ensure the building reference is up to date
          existingLine.factoryBuildingId = building.id;
          // console.log('[FactoryProduction] ✅ Existing production line:', {
          //   lineId,
          //   productId: existingLine.productId,
          //   allocatedEmployees: existingLine.allocatedEmployees,
          //   status: existingLine.status,
          //   pauseReason: existingLine.pauseReason
          // });
        }
      }
    });
    
    // console.log('[FactoryProduction] 📊 Production line summary:', {
    //   factoriesFound,
    //   linesCreated,
    //   totalLines: productionLines.size
    // });
    
    // Recalculate production metrics for all lines if products provided
    if (products) {
      let linesUpdated = 0;
      productionLines.forEach(line => {
        if (line.productId || line.allocatedEmployees > 0) {
          this.updateLineProduction(line, products);
          linesUpdated++;
        }
      });
      // console.log('[FactoryProduction] 🔄 Updated production metrics for', linesUpdated, 'lines');
    }
  }

  // Get all production lines for a specific factory
  getFactoryProductionLines(factoryId: string): ProductionLine[] {
    const store = this.getStore();
    const lines: ProductionLine[] = [];
    store.productionLines.forEach(line => {
      if (line.factoryBuildingId === factoryId) {
        lines.push(line);
      }
    });
    return lines.sort((a, b) => a.lineNumber - b.lineNumber);
  }

  // Get all production lines
  getAllProductionLines(): ProductionLine[] {
    const store = this.getStore();
    return Array.from(store.productionLines.values());
  }

  // Get production line assigned to a product
  getProductionLineForProduct(productId: string): ProductionLine | null {
    const store = this.getStore();
    for (const line of store.productionLines.values()) {
      if (line.productId === productId) {
        return line;
      }
    }
    return null;
  }

  // Get factory production summary
  getFactoryProduction(factoryId: string): FactoryProduction {
    const lines = this.getFactoryProductionLines(factoryId);
    const componentConsumption = new Map<string, number>();
    
    let totalDailyOutput = 0;
    let totalEmployees = 0;
    let totalEfficiency = 0;
    let activeLines = 0;
    
    lines.forEach(line => {
      totalEmployees += line.allocatedEmployees;
      if (line.status === 'active') {
        totalDailyOutput += line.dailyOutput;
        totalEfficiency += line.efficiency;
        activeLines++;
        
        // Calculate component consumption for this line
        if (line.productId) {
          // This will be populated when we integrate with product data
        }
      }
    });
    
    return {
      factoryId,
      productionLines: lines,
      totalDailyOutput,
      totalEmployees,
      averageEfficiency: activeLines > 0 ? totalEfficiency / activeLines : 0,
      componentConsumption
    };
  }

  // Assign a product to a production line
  assignProductToLine(
    lineId: string, 
    productId: string | null,
    products: Product[],
    gameDate: { day: number; month: number; year: number },
    buildings?: OwnedBuilding[]
  ): { success: boolean; message?: string } {
    const store = this.getStore();
    const line = store.productionLines.get(lineId);
    if (!line) {
      return { success: false, message: 'Production line not found' };
    }
    
    // Check if line can be changed (6-month cooldown)
    if (line.lastProductChange && productId !== null) {
      const monthsSinceChange = this.getMonthsDifference(line.lastProductChange, gameDate);
      if (monthsSinceChange < 6) {
        const remainingMonths = 6 - monthsSinceChange;
        return { 
          success: false, 
          message: `Production line locked for ${remainingMonths} more months` 
        };
      }
    }
    
    // Find product details
    const product = productId ? products.find(p => p.id === productId) : null;
    
    // Update buildings cache if provided
    if (buildings) {
      this.buildingsCache.clear();
      buildings.forEach(b => this.buildingsCache.set(b.id, b));
    }
    
    // Update the line using the store action
    const updatedLine = {
      ...line,
      productId: productId,
      productName: product?.name,
      lastProductChange: productId ? { ...gameDate } : undefined
    };
    
    if (!productId) {
      updatedLine.status = 'idle';
      updatedLine.pauseReason = 'no_product';
      updatedLine.dailyOutput = 0;
      updatedLine.efficiency = 0;
      updatedLine.qualityScore = 0;
    } else {
      // Recalculate production metrics
      this.updateLineProduction(updatedLine, products);
    }
    
    // Update the store
    store.assignProductToLine(lineId, productId, product?.name, gameDate);
    
    return { success: true };
  }

  // Allocate employees to a production line
  allocateEmployeesToLine(
    lineId: string,
    employeeCount: number,
    maxCapacity: number,
    products: Product[],
    buildings?: OwnedBuilding[]
  ): { success: boolean; message?: string } {
    const store = this.getStore();
    const line = store.productionLines.get(lineId);
    if (!line) {
      return { success: false, message: 'Production line not found' };
    }
    
    if (employeeCount > maxCapacity) {
      return { 
        success: false, 
        message: `Cannot allocate ${employeeCount} employees. Maximum capacity is ${maxCapacity}` 
      };
    }
    
    // Update buildings cache if provided
    if (buildings) {
      this.buildingsCache.clear();
      buildings.forEach(b => this.buildingsCache.set(b.id, b));
    }
    
    // Create updated line with new employee count
    const updatedLine = {
      ...line,
      allocatedEmployees: employeeCount
    };
    
    // Update production metrics
    this.updateLineProduction(updatedLine, products);
    
    // Update the store with the new employee count and production metrics
    store.updateProductionLine(lineId, updatedLine);
    
    return { success: true };
  }

  // Update production calculations for a line
  private updateLineProduction(line: ProductionLine, products: Product[]): void {
    const store = this.getStore();
    
    if (!line.productId || line.allocatedEmployees === 0) {
      line.status = line.productId ? 'paused' : 'idle';
      line.pauseReason = line.productId ? 'no_employees' : 'no_product';
      line.dailyOutput = 0;
      line.efficiency = 0;
      line.qualityScore = 0;
      // Update the store with the new values
      store.updateProductionLine(line.id, line);
      return;
    }
    
    const building = this.getFactoryBuilding(line.factoryBuildingId);
    if (!building) return;
    
    const buildingType = buildingService.getBuildingType(building.buildingTypeId);
    if (!buildingType || !buildingType.productionCapacity) return;
    
    const product = products.find(p => p.id === line.productId);
    if (!product) return;
    
    // Calculate base production using centralized formula
    const baseCapacity = buildingType.productionCapacity;
    const optimalEmployees = Math.floor(buildingType.capacity.factoryWorkers / (buildingType.productionLines || 1));

    const { efficiency, dailyOutput } = GAME_FORMULAS.production.calculateProductionOutput(
      baseCapacity,
      line.allocatedEmployees,
      optimalEmployees
    );

    line.efficiency = efficiency;
    line.dailyOutput = dailyOutput;
    line.qualityScore = product.finalQuality ? Math.round(product.finalQuality * 100) : 80; // Will be enhanced with quality control
    
    // Update status
    line.status = 'active';
    line.pauseReason = undefined;
    
    // Update the store with the new values
    store.updateProductionLine(line.id, line);
  }

  // Get the factory building for a production line
  private getFactoryBuilding(buildingId: string): OwnedBuilding | null {
    return this.buildingsCache.get(buildingId) || null;
  }

  // Calculate months difference between two dates
  private getMonthsDifference(
    date1: { day: number; month: number; year: number },
    date2: { day: number; month: number; year: number }
  ): number {
    const months1 = date1.year * 12 + date1.month;
    const months2 = date2.year * 12 + date2.month;
    return Math.abs(months2 - months1);
  }

  // Process daily production (called by game loop)
  // This now returns information about what COULD be produced, but doesn't actually produce
  // Actual production is handled by the manufacturing service
  async processDailyProduction(
    products: Product[],
    employees: Employee[],
    buildings: OwnedBuilding[],
    gameDate: { day: number; month: number; year: number }
  ): Promise<{
    productionCapacity: Map<string, number>;
    constraints: ProductionConstraint[];
    manufacturingStarted: Array<{ productId: string; quantity: number }>;
    linesResumed: Array<{ productId: string; lineId: string }>;
  }> {
    // console.log('[FactoryProduction] 📅 Processing daily production:', {
    //   date: `${gameDate.month}/${gameDate.day}/${gameDate.year}`,
    //   productCount: products.length,
    //   employeeCount: employees.length,
    //   buildingCount: buildings.length,
    //   productionLineCount: this.getStore().productionLines.size
    // });
    
    const store = this.getStore();
    const productionCapacity = new Map<string, number>();
    const constraints: ProductionConstraint[] = [];
    const manufacturingStarted: Array<{ productId: string; quantity: number }> = [];
    const linesResumed: Array<{ productId: string; lineId: string }> = [];
    
    // Get current inventory state from the service
    const inventoryState = await inventoryManagementService.initializeInventory(products);
    const componentInventory = inventoryState.components;
    
    // console.log('[FactoryProduction] 📦 Component inventory loaded:', {
    //   componentCount: componentInventory.size,
    //   totalComponentStock: Array.from(componentInventory.values()).reduce((sum, inv) => sum + inv.currentStock, 0)
    // });
    
    // Update building references
    const buildingMap = new Map(buildings.map(b => [b.id, b]));
    
    // First, check for paused lines that can be resumed
    let pausedLines = 0;
    for (const [lineId, line] of store.productionLines) {
      if (line.status === 'paused' && line.pauseReason === 'no_components' && line.productId) {
        pausedLines++;
        const product = products.find(p => p.id === line.productId);
        if (!product || !product.designConfig?.components) continue;
        
        // console.log('[FactoryProduction] 🔍 Checking paused line for resumption:', {
        //   lineId,
        //   productId: line.productId,
        //   productName: product.name,
        //   pauseReason: line.pauseReason
        // });
        
        // Check if components are now available
        const monthlyCapacity = line.dailyOutput * 30;
        const manufacturingConstraints = await manufacturingService.checkManufacturingConstraints(
          product,
          monthlyCapacity,
          line,
          employees,
          buildings
        );
        
        // console.log('[FactoryProduction] 🔍 Constraint check result:', {
        //   lineId,
        //   hasComponents: manufacturingConstraints.hasComponents,
        //   hasWorkers: manufacturingConstraints.hasWorkers,
        //   allocatedEmployees: line.allocatedEmployees,
        //   canResume: manufacturingConstraints.hasComponents && line.allocatedEmployees > 0
        // });
        
        if (manufacturingConstraints.hasComponents && line.allocatedEmployees > 0) {
          // Resume the production line
          const updatedLine = {
            ...line,
            status: 'active' as const,
            pauseReason: undefined
          };
          store.updateProductionLine(lineId, updatedLine);
          
          linesResumed.push({
            productId: product.id,
            lineId: line.id
          });
          
          // console.log(`[FactoryProduction] ✅ Production line ${lineId} resumed for product ${product.name}`);
        }
      }
    }
    
    // console.log('[FactoryProduction] 📊 Paused lines checked:', {
    //   pausedLinesFound: pausedLines,
    //   linesResumed: linesResumed.length
    // });
    
    // Step 1: Aggregate production capacity per product from ALL active lines
    // This ensures we sum output from multiple factories producing the same product
    const productAggregation = new Map<string, {
      totalDailyOutput: number;
      lines: Array<{ lineId: string; line: ProductionLine }>;
      product: Product;
    }>();

    let activeLines = 0;
    let idleLines = 0;

    for (const [lineId, line] of store.productionLines) {
      if (line.status === 'idle') idleLines++;
      if (line.status !== 'active' || !line.productId) {
        continue;
      }

      // Check if building is available
      const building = buildingMap.get(line.factoryBuildingId);
      if (!building || building.isUnderConstruction || building.isUpgrading) {
        const updatedLine = {
          ...line,
          status: 'paused' as const,
          pauseReason: 'maintenance' as const
        };
        store.updateProductionLine(lineId, updatedLine);
        continue;
      }

      const product = products.find(p => p.id === line.productId);
      if (!product || !product.designConfig?.components) {
        continue;
      }

      activeLines++;

      // Aggregate this line's output with other lines for the same product
      const existing = productAggregation.get(line.productId) || {
        totalDailyOutput: 0,
        lines: [],
        product
      };
      existing.totalDailyOutput += line.dailyOutput;
      existing.lines.push({ lineId, line });
      productAggregation.set(line.productId, existing);
    }

    // Step 2: Process each product - check each line's constraints independently
    // Lines operate independently: only lines that pass constraints contribute to output
    for (const [productId, aggregation] of productAggregation) {
      const { lines, product } = aggregation;

      // Defensive check for empty lines array
      if (lines.length === 0) {
        continue;
      }

      // Check if this product already has active manufacturing
      const activeProduction = manufacturingService.getActiveProduction(productId);
      if (activeProduction) {
        // Log constraint for visibility - helps users understand why lines aren't starting new batches
        constraints.push({
          type: 'capacity_limit',
          affectedProducts: [productId],
          severity: 'info',
          message: `${product.name} already in manufacturing (Batch: ${activeProduction.id})`,
          estimatedImpact: 0
        });
        continue;
      }

      // Check each line's constraints individually
      // Lines may have different employee counts, efficiency levels, and factory buildings
      const passingLines: Array<{ lineId: string; line: ProductionLine; dailyOutput: number }> = [];
      const failingLines: Array<{ lineId: string; line: ProductionLine; reason: ManufacturingConstraints }> = [];

      for (const { lineId, line } of lines) {
        const lineMonthlyCapacity = line.dailyOutput * GAME_CONSTANTS.time.daysPerMonth;

        const lineConstraints = await manufacturingService.checkManufacturingConstraints(
          product,
          lineMonthlyCapacity,
          line,
          employees,
          buildings
        );

        if (lineConstraints.canProduce) {
          passingLines.push({ lineId, line, dailyOutput: line.dailyOutput });
        } else {
          failingLines.push({ lineId, line, reason: lineConstraints });
        }
      }

      // Update failing lines' status based on their individual constraint results
      for (const { lineId, line, reason } of failingLines) {
        if (!reason.hasComponents) {
          const updatedLine = {
            ...line,
            status: 'paused' as const,
            pauseReason: 'no_components' as const
          };
          store.updateProductionLine(lineId, updatedLine);

          // Add constraint with line-specific message
          if (reason.missingComponents) {
            for (const [componentId, shortage] of reason.missingComponents) {
              const inventory = componentInventory.get(componentId);
              constraints.push({
                type: 'component_shortage',
                affectedProducts: [product.id],
                severity: 'critical',
                message: `Line ${line.lineNumber}: Need ${shortage.required} ${inventory?.componentName || componentId}, have ${shortage.available}`,
                estimatedImpact: line.dailyOutput
              });
            }
          }
        } else if (!reason.hasWorkers) {
          const updatedLine = {
            ...line,
            status: 'paused' as const,
            pauseReason: 'no_employees' as const
          };
          store.updateProductionLine(lineId, updatedLine);

          constraints.push({
            type: 'employee_shortage',
            affectedProducts: [product.id],
            severity: 'warning',
            message: reason.message || `Line ${line.lineNumber}: Insufficient workers`,
            estimatedImpact: line.dailyOutput
          });
        } else if (!reason.hasCapacity) {
          constraints.push({
            type: 'capacity_limit',
            affectedProducts: [product.id],
            severity: 'warning',
            message: reason.message || `Line ${line.lineNumber}: Factory at capacity`,
            estimatedImpact: 0
          });
        }
      }

      // Start manufacturing with aggregated output from passing lines only
      if (passingLines.length > 0) {
        const passingDailyOutput = passingLines.reduce((sum, l) => sum + l.dailyOutput, 0);
        const passingMonthlyCapacity = passingDailyOutput * GAME_CONSTANTS.time.daysPerMonth;

        productionCapacity.set(productId, passingMonthlyCapacity);

        // Use the first passing line's ID for the manufacturing batch
        const result = await manufacturingService.startManufacturing(
          product,
          passingMonthlyCapacity,
          passingLines[0].lineId,
          gameDate
        );

        if (result.success) {
          manufacturingStarted.push({
            productId: product.id,
            quantity: passingMonthlyCapacity
          });
        }
      }
    }
    
    // console.log('[FactoryProduction] 📊 Daily production processing complete:', {
    //   date: `${gameDate.month}/${gameDate.day}/${gameDate.year}`,
    //   activeLines,
    //   idleLines,
    //   productionCapacityEntries: productionCapacity.size,
    //   constraintsFound: constraints.length,
    //   manufacturingStarted: manufacturingStarted.length,
    //   linesResumed: linesResumed.length,
    //   totalProductionLines: store.productionLines.size
    // });
    
    // Log all production lines status summary
    const lineStatusSummary = new Map<string, number>();
    store.productionLines.forEach(line => {
      const key = `${line.status}${line.pauseReason ? `-${line.pauseReason}` : ''}`;
      lineStatusSummary.set(key, (lineStatusSummary.get(key) || 0) + 1);
    });
    
    // console.log('[FactoryProduction] 📋 Production line status summary:', 
    //   Object.fromEntries(lineStatusSummary)
    // );
    
    return { productionCapacity, constraints, manufacturingStarted, linesResumed };
  }

  // Update production metrics for a product
  private updateProductionMetrics(
    productId: string,
    dailyProduction: number,
    qualityScore: number,
    _componentConsumption: Map<string, number>
  ): void {
    const existing = this.productionMetrics.get(productId) || {
      productId,
      dailyProduction: 0,
      monthlyProduction: 0,
      totalProduced: 0,
      averageQuality: qualityScore,
      defectRate: 0,
      efficiency: 0,
      componentCostPerUnit: 0,
      laborCostPerUnit: 0,
      totalCostPerUnit: 0
    };
    
    // Update metrics (simplified for now)
    existing.dailyProduction = dailyProduction;
    existing.monthlyProduction = dailyProduction * 30;
    existing.totalProduced += dailyProduction;
    existing.averageQuality = qualityScore;
    
    this.productionMetrics.set(productId, existing);
  }

  // Get production metrics for a product
  getProductionMetrics(productId: string): ProductionMetrics | null {
    return this.productionMetrics.get(productId) || null;
  }

  // Get all production constraints
  checkProductionConstraints(
    products: Product[],
    componentInventory: Map<string, ComponentInventory>,
    _employees: Employee[]
  ): ProductionConstraint[] {
    const store = this.getStore();
    const constraints: ProductionConstraint[] = [];
    
    // Check each production line
    store.productionLines.forEach(line => {
      if (!line.productId) return;
      
      const product = products.find(p => p.id === line.productId);
      if (!product) return;
      
      // Check employee constraints
      if (line.allocatedEmployees === 0) {
        constraints.push({
          type: 'employee_shortage',
          affectedProducts: [product.id],
          severity: 'warning',
          message: `No employees allocated to production line for ${product.name}`,
          estimatedImpact: line.dailyOutput
        });
      }
      
      // Check component constraints (simplified)
      if (product.designConfig?.components) {
        Object.values(product.designConfig.components).forEach(componentId => {
          const inventory = componentInventory.get(componentId);
          if (inventory && inventory.currentStock < line.dailyOutput * 7) {
            constraints.push({
              type: 'component_shortage',
              affectedProducts: [product.id],
              severity: 'warning',
              message: `Low stock of ${inventory.componentName} for ${product.name} (less than 1 week)`,
              estimatedImpact: 0
            });
          }
        });
      }
    });
    
    return constraints;
  }

  // Get production schedule for planning
  getProductionSchedule(productId: string): ProductionSchedule | null {
    const store = this.getStore();
    const lines = Array.from(store.productionLines.values()).filter(l => l.productId === productId);
    if (lines.length === 0) return null;
    
    const targetUnitsPerDay = lines.reduce((sum, line) => sum + line.dailyOutput, 0);
    const actualUnitsPerDay = lines.filter(l => l.status === 'active').reduce((sum, line) => sum + line.dailyOutput, 0);
    const employeeRequirements = lines.reduce((sum, line) => sum + line.allocatedEmployees, 0);
    
    return {
      productId,
      targetUnitsPerDay,
      actualUnitsPerDay,
      componentRequirements: new Map(), // Will be populated when integrated
      employeeRequirements,
      constraints: []
    };
  }

  // Stop all production lines for a discontinued product
  stopProductionForProduct(productId: string): void {
    const store = this.getStore();
    let linesAffected = 0;
    
    // Find all production lines assigned to this product
    store.productionLines.forEach(line => {
      if (line.productId === productId) {
        // Create updated line with cleared product assignment
        const updatedLine = {
          ...line,
          productId: null,
          productName: undefined,
          status: 'idle' as const,
          pauseReason: 'no_product' as const,
          dailyOutput: 0,
          efficiency: 0,
          qualityScore: 0
          // Note: We don't reset allocatedEmployees or lastProductChange
          // as these should persist even when the product is removed
        };
        
        store.updateProductionLine(line.id, updatedLine);
        linesAffected++;
      }
    });
    
    if (linesAffected > 0) {
      // console.log(`Stopped ${linesAffected} production line(s) for discontinued product ${productId}`);
    }
  }

  // Clear all production data (for testing/reset)
  clearAllProduction(): void {
    const store = this.getStore();
    store.productionLines.clear();
    this.productionMetrics.clear();
    this.buildingsCache.clear();
  }
}

export const factoryProductionService = FactoryProductionService.getInstance();